import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { getDashboardBySlug } from "@/features/shared/navigation";
import { usePublishedDashboardsQuery } from "@/features/shared/hooks/queries";
import { useMaritimeWarningPageDefaults } from "@/features/shared/hooks/use-warning-page-defaults";
import { getIsAuthenticated } from "@/features/shared/features/authentication";
import type { MaritimeWarningDomainId } from "@/lib/maritime-domains";
import {
  readMaritimeWarningPageSettings,
  visibleMaritimeDomainIds,
} from "@/lib/maritime-warning-page-settings.utils";

const NO_DOMAINS: MaritimeWarningDomainId[] = [];

export function useDashboardWarningSettings() {
  const params = useParams();
  const authenticated = getIsAuthenticated();
  const dashboardsQuery = usePublishedDashboardsQuery();
  const maritimeDefaults = useMaritimeWarningPageDefaults();
  const selectedDashboard = authenticated
    ? getDashboardBySlug(dashboardsQuery.data, params.dashboard)
    : undefined;

  const maritimeWarningPage = readMaritimeWarningPageSettings(
    selectedDashboard?.maritime_warnings,
    maritimeDefaults,
  );
  // Stable reference so consumers' memos (e.g. toolbar warning color) actually hit.
  // Empty while the page is off, so map and toolbar follow the master switch.
  const enabledKey = visibleMaritimeDomainIds(maritimeWarningPage).join(",");
  const enabledMaritimeDomains = useMemo(
    () => (enabledKey ? (enabledKey.split(",") as MaritimeWarningDomainId[]) : NO_DOMAINS),
    [enabledKey],
  );

  if (!authenticated) {
    return {
      selectedDashboard: undefined,
      publicWarningSettings: undefined,
      maritimeWarningPage: readMaritimeWarningPageSettings(undefined, maritimeDefaults),
      enabledMaritimeDomains: NO_DOMAINS,
    };
  }

  return {
    selectedDashboard,
    publicWarningSettings: selectedDashboard?.public_warnings_settings,
    maritimeWarningPage,
    enabledMaritimeDomains,
  };
}
